import { PAYWALL_STEPS } from "@/components/paywall/PaywallPage";
import AuditableEntity from "@/types/AuditableEntity";
import { AddOn } from "./AddOn";

export type Subscription = AuditableEntity & {
  name: string;
  description?: string;
  objectKey: string;
  seatLimit: number;
  monthlyPrice: number;
  addOns: AddOn[];
  canBuy: AddOn[];
};

export type BuyingSubscription = Subscription & {
  addedAddOns: (AddOn & { alreadyPurchased?: boolean })[];
  billingPeriod: SUBSCRIPTION_BILLING_PERIOD;
  acceptedTermsAndConditions: boolean;
  billingContactData?: BillingContactData;
  step: PAYWALL_STEPS;
};

export enum SUBSCRIPTION_BILLING_PERIOD {
  MONTHLY = "MONTHLY",
  YEARLY = "YEARLY",
}

export type BillingContactData = {
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  country: string;
  address: string;
  aptSuite: string;
  city: string;
  state: string;
  postalCode: string;
};

export enum SubscriptionType {
  SOLO = "solo",
  BASIC = "basic",
  CORE = "core",
  GROWTH = "growth",
  SCALE = "scale",
}

// usd
export const FIXED_DISCOUNT_ANNUALLY = 10.0;

export function filterPaidSubscriptions(subs: Subscription[]): Subscription[] {
  return subs.filter((s) => ![SubscriptionType.SOLO, SubscriptionType.BASIC].includes(s.objectKey as SubscriptionType));
}

export function isPaidSubscription(sub: Subscription): boolean {
  return ![SubscriptionType.SOLO, SubscriptionType.BASIC].includes(sub.objectKey as SubscriptionType);
}

export const calculateExtraAddOnsMonthlyTotal = (selectedSubscription: BuyingSubscription) => {
  const total = selectedSubscription.addedAddOns.reduce((a, b) => a + b.monthlyIndividualPrice, 0);
  return parseFloat(total.toFixed(2));
};

const calculateSubscriptionPurchaseTotalMonthly = (selectedSubscription: BuyingSubscription): number => {
  return calculateExtraAddOnsMonthlyTotal(selectedSubscription) + selectedSubscription.monthlyPrice;
};

const calculateSubscriptionPurchaseTotalAnnually = (selectedSubscription: BuyingSubscription): number => {
  return (calculateExtraAddOnsMonthlyTotal(selectedSubscription) + getSubscriptionPrice(selectedSubscription, false)) * 12;
};

export const subscriptionPurchaseTotalByBillingPeriod = {
  [SUBSCRIPTION_BILLING_PERIOD.YEARLY]: calculateSubscriptionPurchaseTotalAnnually,
  [SUBSCRIPTION_BILLING_PERIOD.MONTHLY]: calculateSubscriptionPurchaseTotalMonthly,
};

export function getSubscriptionPrice(sub: Subscription, monthly: boolean) {
  if (!sub) return 0;

  if (monthly) {
    return sub.monthlyPrice;
  }

  return Math.max(sub.monthlyPrice - FIXED_DISCOUNT_ANNUALLY, 0);
}

export function getSubscriptionPriceWithDiscount(monthlyPrice: number, monthly: boolean) {
  if (!monthlyPrice) return 0;

  if (monthly) {
    return monthlyPrice;
  }

  return Math.max(monthlyPrice - FIXED_DISCOUNT_ANNUALLY, 0);
}
